import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import BrandMark from "../components/BrandMark";
import { SiteFooter } from "../components/Layout";
import { api } from "../lib/api";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => { if (!token) nav("/auth?mode=login"); }, [token, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, newPassword: pw });
      toast.success("Password updated — please sign in");
      nav("/auth?mode=login");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="dark-theme min-h-screen flex flex-col">
      <div className="flex-1 flex">
        <div className="hidden md:block w-1/2 relative">
          <img
            src="https://images.unsplash.com/photo-1653307370781-a5b033bac658?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/40 to-background" />
          <Link to="/" className="absolute top-8 left-8 flex items-center gap-3" data-testid="reset-brand">
            <BrandMark size={40} />
            <span className="font-serif text-2xl">cauldron</span>
          </Link>
        </div>
        <div className="w-full md:w-1/2 flex items-center justify-center p-8 py-12">
          <div className="w-full max-w-md space-y-6">
            <Link to="/" className="md:hidden flex items-center gap-3 mb-2" data-testid="reset-brand-mobile">
              <BrandMark size={36} />
              <span className="font-serif text-2xl">cauldron</span>
            </Link>
            <div>
              <div className="label-mystic mb-2">New Password</div>
              <h1 className="font-serif text-5xl">Choose a new password</h1>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="New password"
                  className="w-full bg-input border border-secondary/30 p-3 pr-12 rounded-sm focus:outline-none focus:border-secondary"
                  data-testid="reset-pw-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/55 hover:text-foreground p-1"
                  aria-label={show ? "Hide password" : "Show password"}
                  data-testid="reset-pw-toggle"
                  tabIndex={-1}
                >
                  {show ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                </button>
              </div>
              <input
                type={show ? "text" : "password"}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                placeholder="Confirm password"
                className="w-full bg-input border border-secondary/30 p-3 rounded-sm focus:outline-none focus:border-secondary"
                data-testid="reset-pw2-input"
                required
              />
              <button
                disabled={busy}
                className="w-full bg-primary text-primary-foreground py-3 rounded-sm uppercase tracking-wider text-xs disabled:opacity-50"
                data-testid="reset-submit-btn"
              >
                {busy ? "Updating..." : "Update Password"}
              </button>
            </form>
            <div className="text-center text-sm text-foreground/60">
              Remembered? <Link to="/auth?mode=login" className="text-secondary hover:text-secondary/80" data-testid="reset-back-login">Back to sign in</Link>
            </div>
          </div>
        </div>
      </div>
      <SiteFooter withBottomNavOffset={false} />
    </div>
  );
}
